import React, { useState, useEffect, useRef } from 'react';
import './ExportExtra.css';
import { getCreators, saveCreators } from './creators';
import { getPersonas, savePersonas, sanitizePersona } from './personas';
import PerfBlockHeader from './perf/PerfBlockHeader';
import RenderBar from './components/RenderBar';
import { startExportRun } from './exportRun';
import { useExportRun, claimExportRun, releaseExportRun, registerExportTab, resetRunView, clearDone, consumePending } from './exportRunStore';
import { registerPresenter } from './scanExportStore';
import { keyOf, sharedValue, rangeKeys, toggleAll, duplicateKeys, tileStatus, biggestConcept } from './exportSelection';
import { ALL_FEATURES, SHORTCUT_COUNT, getActiveFeatures, saveActiveFeatures, defaultFeature } from './exportFeatures';
import { DEFAULT_CREATOR_TYPE } from './creatorType';
import { NAME_FORMAT_V2 } from './namingConfig';
import { getPlatformTags, savePlatformTags, tagSuffix, newTagId } from './exportPlatformTags';

// ── Creative ID minting (v4.03 Phase 4) ───────────────────────────────
// Phase 4 suspension (2026-06-15) — CID retiré du nom exporté. Le minting
// et l'event log continuent. Flip ce flag à `true` pour réintroduire le
// segment `_CID-TBxxxxxx` dans le filename. Aucun autre changement requis.
const INJECT_CID_IN_NAME = false;
function mintCreativeId() {
  // 'TB' + 6 chars Crockford base32 (sans I/L/O/U). ~1.07e9 combinaisons —
  // collisions négligeables à notre volume.
  const ALPH = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const buf = new Uint8Array(6);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  let s = '';
  for (let i = 0; i < 6; i++) s += ALPH[buf[i] % 32];
  return 'TB' + s;
}

// ── Event log POST helper (v4.03 Phase 4) ─────────────────────────────
const EVENTS_API = process.env.REACT_APP_API_URL || 'https://timbo-production.up.railway.app';
function postEvent(payload) {
  // Fire-and-forget : le logging d'event ne doit JAMAIS bloquer ni casser un export.
  try {
    fetch(`${EVENTS_API}/api/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(r => { if (!r.ok) console.warn('[events] rejected', r.status); })
      .catch(e => console.warn('[events] failed', e.message));
  } catch (e) { console.warn('[events] threw', e.message); }
}

// ── Feature tags (built-in) ────────────────────────────────────────────
const BUILTIN_FEATURE_TAGS = {
  ghost_mannequin: 'IMGT-GHOST',
  product_staging:  'IMGT-STAGE',
  animated_design:  'ANIM',
  ai_backgrounds:   'AIBG',
  virtual_models:   'IMGT-MODEL',
  flat_lay:         'IMGT-FLATLAY',
};

const LS_CUSTOM_FEATURES = 'timbo_custom_features';
export function getCustomFeatures() {
  try { return JSON.parse(localStorage.getItem(LS_CUSTOM_FEATURES) || '[]'); } catch { return []; }
}
export function saveCustomFeatures(arr) {
  try { localStorage.setItem(LS_CUSTOM_FEATURES, JSON.stringify(arr)); } catch {}
}

function buildFeatureTags() {
  const custom = getCustomFeatures();
  const result = { ...BUILTIN_FEATURE_TAGS };
  for (const cf of custom) {
    if (cf.key && cf.tag) result[cf.key] = cf.tag;
  }
  return result;
}

const LANGS = ['en', 'es', 'pt'];
const ZOOM_LEVELS = [4, 5, 6, 7, 8, 10];
const DEFAULT_ZOOM = 2;

function durTier(durationSec) {
  if (!durationSec || durationSec <= 15) return 15;
  if (durationSec <= 30) return 30;
  return 60;
}

// v2: <type>-<Creator>_<Name>-<n>_HOOK-ugc|aigc_DUR-xx_P-<persona>_<DIM>_[FEATURE]_[lang]
// A skipped block is a bare "-" so the block count stays stable. Statics: DUR-0 + STATIC-<dim>.
// CAT and the "Clean" suffix of v1 are not part of the generator's format. CID stays out (INJECT_CID_IN_NAME applies to v1 only).
function buildNameV2(entry, idx, params, { featureTag, overrides }) {
  const ov  = overrides[idx] || {};
  const ext = entry.ext || 'mp4';
  const staticDim = params.dim && params.dim !== 'PO';           // a STATIC-<dim> choice from the form
  const isStatic = /^(png|jpe?g|webp|gif)$/i.test(ext) || staticDim;
  const durOverride = params.dur && params.dur !== 'auto' ? Number(params.dur) : null;
  const dur = isStatic ? 0 : (durOverride ?? durTier(ov.duration ?? entry.duration));
  const hook = params.hookType === 'aigc' ? 'aigc' : 'ugc';
  const dim = isStatic ? (staticDim ? `STATIC-${params.dim}` : '-') : 'PO';
  const blocks = [
    `${DEFAULT_CREATOR_TYPE}-${params.creator}`,
    `${params.creativeName.trim()}-${params.n ?? idx + 1}`,
    `HOOK-${hook}`,
    `DUR-${dur}`,
    params.persona ? `P-${params.persona}` : '-',
    dim,
    featureTag ? `[${featureTag}]` : '-',
    params.lang ? `[${params.lang}]` : '-',
  ];
  return `${blocks.join('_')}.${ext}`;
}

// A tile's form values -> builder params. An active platform tag appends its suffix to the creative name.
export function paramsFromMeta(meta, tags) {
  const m = meta || {};
  const suffix = tagSuffix(tags);
  return { ...m, creativeName: (m.creativeName || '').trim() ? `${m.creativeName.trim()}${suffix}` : '' };
}

// `dc`: whole-batch [DC] switch (Yann's manual toggle). Stamps `[DC]-` at the front of the creative-name block of every output.
export function buildNameFromStaged(entry, idx, stagedParams, { featureTag, overrides, dc = false }) {
  const params = { ...(stagedParams || {}) };
  if (dc && params.creativeName && params.creativeName.trim()) params.creativeName = `[DC]-${params.creativeName.trim()}`;
  const creator = params.creator || '';
  const creativeName = params.creativeName || '';
  if (!creator || !creativeName.trim()) return 'Fill Creator + Creative Name';
  if (NAME_FORMAT_V2) return buildNameV2(entry, idx, params, { featureTag, overrides });
  const ov  = overrides[idx] || {};
  const num = idx + 1;
  const dur = durTier(ov.duration ?? entry.duration);
  const p   = params.persona || 'broad';
  const cat = params.cat || 'home';
  const lang = params.lang || 'en';
  const ext = entry.ext || 'mp4';
  const ft  = featureTag || 'AIBG';
  const cid = (INJECT_CID_IN_NAME && params.creativeId) ? `_CID-${params.creativeId}` : '';
  const isClean = idx === 0;
  if (isClean) {
    return `${DEFAULT_CREATOR_TYPE}-${creator}_${creativeName.trim()}Clean-${num}_HOOK-ugc_DUR-${dur}_PERSONA-${p}_CAT-${cat}${cid}_PO_[${ft}]_[${lang}].${ext}`;
  }
  return `${DEFAULT_CREATOR_TYPE}-${creator}_${creativeName.trim()}-${num}_HOOK-ugc_DUR-${dur}_PERSONA-${p}_CAT-${cat}${cid}_PO_[${ft}]_[${lang}].${ext}`;
}

// ── FS API helpers ─────────────────────────────────────────────────
// (fetch / save helpers moved to exportRun.js with the run itself)
async function pickDirectory() {
  if (typeof window === 'undefined' || !('showDirectoryPicker' in window)) return null;
  try { return await window.showDirectoryPicker({ mode: 'readwrite' }); }
  catch (err) { if (err.name === 'AbortError') return null; return null; }
}

// ── VideoModal ─────────────────────────────────────────────────────
function VideoModal({ src, name, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div className="video-modal-backdrop" onClick={onClose}>
      <div className="video-modal-inner" onClick={e => e.stopPropagation()}>
        <div className="video-modal-header">
          <span className="video-modal-name">{name}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <video src={src} className="video-modal-player" controls autoPlay />
      </div>
    </div>
  );
}

// ── Progress message helper ───────────────────────────────────────
function exportProgressMessage({ done, total, current, phase }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  if (pct >= 40 && pct < 60 && phase !== 'saving') {
    return `${done}/${total} — Timbo is packing your order, get ready!`;
  }
  if (phase === 'preparing') return `${done}/${total} — Preparing…`;
  if (phase === 'downloading') return `${done}/${total} — ${current.slice(0, 45)}`;
  if (phase === 'saving') return `${done}/${total} — Saving ✓`;
  if (current) return `${done}/${total} — ${current.slice(0, 50)}`;
  return `${done}/${total}…`;
}

// ── ExportTab ──────────────────────────────────────────────────────
// One card, one form scoped by the SELECTION (same model as the Scan panel): click toggles, shift-click selects a
// range, "select all" toggles; the form describes the selection and a touched field applies to all of it.
const FIELD_DEFAULTS = { creator: '', persona: 'broad', hookType: 'ugc', dur: 'auto', dim: 'PO', lang: 'en' };
const NAMING_MODEL = '<type>-<Creator>_<Name>-<n>_HOOK-ugc|aigc_DUR-xx_P-<persona>_<DIM>_[FEATURE]_[lang]';

// Titled ORIGINAL has no blob: only the remote file the titling pass produced. Untitled ORIGINAL: blob, else its stored upload.
const tileSrc = (entry) => ((entry.isLocal || entry.isOriginal) ? (entry.localUrl || entry.url || entry.storedUrl) : entry.url);

function tileBadge(entry) {
  if (entry.isOriginal) return 'ORIGINAL';
  if (entry.hook) return 'HOOK';
  if (entry.voice && entry.voice !== 'no voice') return 'VOICE';
  if (entry.hasMusic) return 'MUSIC';
  return 'FILE';
}

// onClearAll: also empties Studio's queue + render results (the tiles come from there, so a local reset alone brings them back).
// onExportFinished: Studio drops the renders once an export completed, so Export is empty on the next visit.
export default function ExportTab({ remixResults = [], feature, onBackToStudio, onClearAll, onExportFinished }) {
  const studioTag = buildFeatureTags()[feature];
  const [active, setActive]                 = useState(getActiveFeatures);
  const [playingVideo, setPlayingVideo]     = useState(null);
  const [zoomLevel, setZoomLevel]           = useState(DEFAULT_ZOOM);
  const [renameFiles, setRenameFiles]       = useState([]);
  const [dzDrag, setDzDrag]                 = useState(false);
  const dzCounter = useRef(0);
  const addFileRef = useRef(null);

  const [selected, setSelected]             = useState(new Set());   // tile keys
  const [cursor, setCursor]                 = useState(null);        // keyboard cursor (Scan: arrows + space)
  const lastClickRef = useRef(null);                                  // anchor for shift-click ranges
  const [meta, setMeta]                     = useState({});          // key -> form values of that tile
  const [last, setLast]                     = useState(() => ({ ...FIELD_DEFAULTS, feature: defaultFeature(getActiveFeatures(), studioTag) }));
  const [validated, setValidated]           = useState([]);          // keys that left the grid for the footer queue, in validation order
  const [batchDC, setBatchDC]               = useState(false);       // whole export; manual, off by default, never inferred

  const [creators, setCreators]             = useState(getCreators);
  const [newCreator, setNewCreator]         = useState('');
  const [showNamingModal, setShowNamingModal]   = useState(false);
  const [namingSection, setNamingSection]     = useState('convention');   // section shown in the # sheet
  const [platformTags, setPlatformTags] = useState(getPlatformTags);
  const [newTag, setNewTag]                 = useState('');
  const [personas, setPersonas]             = useState(getPersonas);
  const [newPersona, setNewPersona]         = useState('');

  // The run lives at module level (exportRun.js / exportRunStore.js): leaving this tab does not touch it,
  // and coming back reattaches to it. What this tab shows is read from there.
  const run = useExportRun();
  const { exportedKeys, exportingKey, errorKeys } = run;
  const exporting = run.running, exportDone = run.finished, exportErrors = run.errors;
  const exportProgress = { done: run.done, total: run.total, current: run.current, phase: run.phase };
  useEffect(() => { const a = registerExportTab(); const b = registerPresenter(); return () => { a(); b(); }; }, []);
  // The run ended while this tab was open: the selection it used is spent.
  const wasRunning = useRef(false);
  useEffect(() => { if (wasRunning.current && !run.running) setSelected(new Set()); wasRunning.current = run.running; }, [run.running]); // eslint-disable-line

  const entries = [
    ...renameFiles,
    ...remixResults.map(r => ({ ...r, ext: r.ext || 'mp4' })),
  ];
  const order = entries.map(keyOf);                                   // every entry (index parity with `entries`)
  const validatedSet = new Set(validated);
  const stagedOrder = order.filter(k => !validatedSet.has(k));        // what the grid shows: not validated yet
  const hasEntries = entries.length > 0;
  const blank = () => ({ ...last, creativeName: '' });
  const metaOf = (k) => meta[k] || blank();

  function handleRenameFiles(rawFiles) {
    const vids = Array.from(rawFiles).filter(f => f.type.startsWith('video/') || /\.(mp4|mov|avi|webm|mkv)$/i.test(f.name));
    const added = vids.map(f => ({ file: f, localUrl: URL.createObjectURL(f), ext: f.name.split('.').pop().toLowerCase(), label: f.name, isLocal: true }));
    setRenameFiles(prev => [...prev, ...added]);
    clearDone();
    // Real duration (for the DUR tier) — a dropped file has no metadata otherwise.
    added.forEach(en => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => { const d = v.duration; if (d && isFinite(d)) setRenameFiles(prev => prev.map(x => x.localUrl === en.localUrl ? { ...x, duration: d } : x)); };
      v.src = en.localUrl;
    });
  }

  // Persisted fields are applied to a tile the first time it is selected (never the creative name).
  useEffect(() => {
    setMeta(prev => {
      let changed = false; const next = { ...prev };
      selected.forEach(k => { if (!next[k]) { next[k] = { ...last, creativeName: '' }; changed = true; } });
      return changed ? next : prev;
    });
  }, [selected]); // eslint-disable-line

  // ── Selection (Scan rules) ──
  function toggleKey(k) { setSelected(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; }); }
  function onTileClick(e, k) {
    const anchor = lastClickRef.current;
    const range = e && e.shiftKey && anchor && anchor !== k ? rangeKeys(stagedOrder, anchor, k) : null;
    if (range) setSelected(prev => { const n = new Set(prev); range.forEach(x => n.add(x)); return n; });
    else toggleKey(k);
    lastClickRef.current = k; setCursor(k);
  }
  const selectAll = () => setSelected(toggleAll(stagedOrder, selected));
  const clearSelection = () => { setSelected(new Set()); setCursor(null); };
  const selKeys = stagedOrder.filter(k => selected.has(k));
  const multi = selKeys.length > 1;
  const selMetas = selKeys.map(metaOf);
  const form = (field) => selKeys.length ? sharedValue(selMetas, field) : { mixed: false, value: last[field] };

  // A touched field applies to the whole selection; everything but the creative name also becomes the persisted draft.
  function applyField(field, value) {
    setMeta(prev => { const next = { ...prev }; selKeys.forEach(k => { next[k] = { ...(next[k] || blank()), [field]: value }; }); return next; });
    if (field !== 'creativeName') setLast(l => ({ ...l, [field]: value }));
  }
  // ── Names / status ──
  // Numbering is continuous across the whole export: a validated file keeps the number it got, the selected ones
  // preview the next numbers (-4, -5, … after three validated). Validate makes the preview final.
  const nOf = (k) => validatedSet.has(k) ? ((meta[k] && meta[k].n) || order.indexOf(k) + 1) : validated.length + selKeys.indexOf(k) + 1;
  const nameOfKey = (k, i) => buildNameFromStaged(entries[i], i, { ...paramsFromMeta(metaOf(k), platformTags), n: nOf(k) }, { featureTag: metaOf(k).feature, overrides: {}, dc: batchDC });
  const candidateNames = {};   // validated + selected tiles only: the rest of the grid has no name yet
  entries.forEach((en, i) => { const k = order[i]; if (!validatedSet.has(k) && !selected.has(k)) return; const m = meta[k]; if (m && (m.creator || '').trim() && (m.creativeName || '').trim()) candidateNames[k] = nameOfKey(k, i); });
  const dups = duplicateKeys(candidateNames);
  const statusOf = (k) => tileStatus(meta[k], dups.has(k));
  const exportKeys = validated.filter(k => order.includes(k));   // what Export sends
  const readyCount = exportKeys.length;
  const bigConcept = biggestConcept(entries);
  // Validate needs every selected tile to have a creator and a unique creative name.
  const canValidate = selKeys.length > 0 && selKeys.every(k => statusOf(k).ready);
  const validateMissing = !selKeys.length ? '' : [...new Set(selKeys.flatMap(k => statusOf(k).missing))]
    .map(m => ({ creator: 'a creator', name: 'a creative name', 'duplicate name': 'a unique name' }[m] || m)).join(' and ');
  function validateSelection() {
    if (!canValidate) return;
    setMeta(prev => { const next = { ...prev }; selKeys.forEach((k, j) => { next[k] = { ...(next[k] || blank()), n: validated.length + j + 1 }; }); return next; });
    setValidated(prev => [...prev, ...selKeys]);
    setSelected(new Set()); setCursor(null);   // the creative name goes with the selection; creator, feature, persona, hook, language carry over via `last`
  }
  const unvalidate = (k) => setValidated(prev => prev.filter(x => x !== k));

  // ── Keyboard (ignored while a field has focus) — same keys as Scan ──
  const kbRef = useRef({});
  kbRef.current = { order: stagedOrder, cursor, selected, active, exporting, readyCount };
  useEffect(() => {
    function onKeyDown(e) {
      const tgt = e.target; const tag = tgt && tgt.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.metaKey || e.ctrlKey || e.altKey) return;
      const s = kbRef.current;
      const digit = /^[1-7]$/.test(e.key) ? parseInt(e.key, 10) : null;
      if (digit && s.active[digit - 1]) { e.preventDefault(); kbApply.current('feature', s.active[digit - 1]); return; }
      if (e.key === 'Escape') { clearSelectionRef.current(); return; }
      if (e.key === 'Enter') { e.preventDefault(); if (!s.exporting && s.readyCount) runExportRef.current(); return; }
      if (!s.order.length) return;
      const idx = s.order.indexOf(s.cursor);
      if (e.key === ' ') { e.preventDefault(); const k = s.order[idx] || s.order[0]; toggleKeyRef.current(k); lastClickRef.current = k; setCursor(k); }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); setCursor(s.order[Math.min((idx < 0 ? -1 : idx) + 1, s.order.length - 1)]); }
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); setCursor(s.order[Math.max((idx < 0 ? 0 : idx) - 1, 0)]); }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []); // eslint-disable-line
  const kbApply = useRef(); kbApply.current = applyField;
  const clearSelectionRef = useRef(); clearSelectionRef.current = clearSelection;
  const toggleKeyRef = useRef(); toggleKeyRef.current = toggleKey;
  const runExportRef = useRef();

  // ── Export ──
  async function runExport() {
    if (!exportKeys.length) return;
    if (!claimExportRun()) return;   // one run at a time, also when you come back to this tab mid-run
    const dirHandle = await pickDirectory();
    if (dirHandle === null && typeof window !== 'undefined' && 'showDirectoryPicker' in window) { releaseExportRun(); return; }
    // Everything the loop needs is resolved here, at click time; the loop itself lives in exportRun.js.
    const jobs = exportKeys.map(k => {
      const i = order.indexOf(k); const entry = entries[i]; const m = metaOf(k); const name = nameOfKey(k, i);
      return {
        key: k, index: i, name, entry,
        event: {
          type: 'export',
          creative_id: mintCreativeId(),
          creator: m.creator,
          creative_name: paramsFromMeta(m, platformTags).creativeName,
          concept: entry.concept || null,
          hook: entry.hook || null,
          script: entry.script || null,
          feature: m.feature,
          persona: m.persona,
          hook_type: m.hookType,
          dc: batchDC,
          cat: entry.category || null,
          lang: m.lang,
          exported_name: name,
          source_url: entry.url || null,
          source_label: entry.label || null,
        },
      };
    });
    // onFinished = Studio drops the renders after a CLEAN run — decided when the run ends, wherever the user is
    // (held until this tab is left if it is open: the done screen stays). It used to run only from "Back to Studio".
    startExportRun({ jobs, dirHandle, postEvent, onFinished: onExportFinished });
  }
  runExportRef.current = runExport;

  function clearAll() {
    setRenameFiles([]); setSelected(new Set()); setMeta({});
    resetRunView(); setCursor(null); setValidated([]);
  }
  function clearAllAndStudio() { clearAll(); if (onClearAll) onClearAll(); }
  // Back to Studio after a clean export: the export is finished, so reset this tab and let Studio drop the renders.
  function backToStudio() {
    if (exportDone && exportErrors.length === 0) { clearAll(); consumePending(); if (onExportFinished) onExportFinished(); }
    if (onBackToStudio) onBackToStudio();
  }
  function addCreatorFn() {
    const v = newCreator.trim();
    if (!v || creators.includes(v)) return;
    const updated = [...creators, v].sort();
    setCreators(updated); saveCreators(updated); setNewCreator('');
  }
  function removeCreatorFn(name) {
    const updated = creators.filter(c => c !== name);
    setCreators(updated); saveCreators(updated);
  }
  function addPersonaFn() {
    const v = sanitizePersona(newPersona);
    if (!v || personas.includes(v)) return;
    const updated = [...personas, v]; setPersonas(updated); savePersonas(updated); setNewPersona('');
  }
  function removePersonaFn(p) { const updated = personas.filter(x => x !== p); setPersonas(updated); savePersonas(updated); }
  function updateTags(next) { setPlatformTags(next); savePlatformTags(next); }
  function addTagFn() {
    const v = newTag.trim();
    if (!v || platformTags.some(t => t.label.toLowerCase() === v.toLowerCase())) return;
    updateTags([...platformTags, { id: newTagId(v, platformTags), label: v, suffix: '', active: false }]); setNewTag('');
  }
  const patchTag = (id, patch) => updateTags(platformTags.map(t => t.id === id ? { ...t, ...patch } : t));
  function toggleFeatureActive(f) {
    const next = active.includes(f) ? active.filter(x => x !== f) : [...active, f];
    if (!next.length) return;
    setActive(next); saveActiveFeatures(next);
  }
  function moveFeature(f, dir) {
    const i = active.indexOf(f); const j = i + dir;
    if (i < 0 || j < 0 || j >= active.length) return;
    const next = [...active]; [next[i], next[j]] = [next[j], next[i]];
    setActive(next); saveActiveFeatures(next);
  }

  const cols = ZOOM_LEVELS[ZOOM_LEVELS.length - 1 - zoomLevel];

  // ── Form pieces ──
  const Mixed = () => <span className="exp2-mixed">mixed</span>;
  const Seg = ({ field, options, label }) => {
    const f = form(field);
    return (
      <div className="export-lang-row">
        {options.map(o => (
          <button key={o} className={`export-lang-btn${!f.mixed && String(f.value) === String(o) ? ' active' : ''}`}
            onClick={() => applyField(field, o)}>{label ? label(o) : o}</button>
        ))}
        {f.mixed && <Mixed />}
      </div>
    );
  };
  const ToggleBtn = ({ field, children }) => {
    const f = form(field);
    return <button className={`export-lang-btn${!f.mixed && f.value ? ' active' : ''}${f.mixed ? ' is-mixed' : ''}`}
      title={f.mixed ? 'mixed — click to set for all' : undefined} onClick={() => applyField(field, f.mixed ? true : !f.value)}>{children}</button>;
  };
  const nameForm = form('creativeName');
  const creatorForm = form('creator');
  const featureForm = form('feature');
  const scopeLabel = selKeys.length === 0 ? 'Nothing selected — these values carry over to the next selection'
    : selKeys.length === 1 ? 'Editing 1 video' : `Editing ${selKeys.length} videos — a touched field applies to all`;

  const openNaming = (section) => { setNamingSection(typeof section === 'string' ? section : 'convention'); setShowNamingModal(true); };
  // Active platform tags, shown under Creative name with the resulting name (single selection with a name).
  const activeTags = platformTags.filter(t => t.active);
  const tagPreview = (() => {
    if (!tagSuffix(platformTags) || selKeys.length !== 1) return '';
    const m = metaOf(selKeys[0]);
    if (!(m.creativeName || '').trim()) return '';
    return `${paramsFromMeta(m, platformTags).creativeName}-${nOf(selKeys[0])}`;
  })();
  // Duration and dimension come from the file (name builder: probed length tier, PO for video); shown as plain text for one tile.
  const detected = selKeys.length === 1 ? (() => {
    const en = entries[order.indexOf(selKeys[0])];
    if (!en) return '';
    if (/^(png|jpe?g|webp|gif)$/i.test(en.ext || '')) return 'static image';
    return `${durTier(en.duration)}s · PO`;
  })() : '';

  return (
    <div className="export-tab tb-page">
      {playingVideo && <VideoModal src={playingVideo.src} name={playingVideo.name} onClose={() => setPlayingVideo(null)} />}

      <div className="tb-card exp2-card">
        <PerfBlockHeader
          icon={<button type="button" className="exp2-hbtn" onClick={openNaming} aria-label="Open the naming convention">#</button>}
          title={<button type="button" className="exp2-hbtn exp2-htitle" onClick={openNaming}>Naming</button>}
          actions={hasEntries ? (
            <>
              <span className="exp2-count">{readyCount} validated · {stagedOrder.length} to go</span>
              <button className="tb-ctl" onClick={() => addFileRef.current && addFileRef.current.click()}>Add more</button>
              <button className="tb-ctl" onClick={clearAllAndStudio}>Clear</button>
            </>
          ) : null}
        />
        <input ref={addFileRef} type="file" multiple accept="video/*,.mp4,.mov,.avi,.webm" style={{ display: 'none' }}
          onChange={e => { handleRenameFiles(e.target.files); e.target.value = ''; }} />

        <div className="exp2-body">
          {/* ── Form (left third) ── */}
          <div className="exp2-form">
            <div className="exp2-scope">{scopeLabel}</div>

            <div className="export-naming-field">
              <label className="staging-field-label">Creator</label>
              <div className="exp2-creator-row">
                <select className="export-select exp2-ctype" value={DEFAULT_CREATOR_TYPE} disabled title="Only influencer is available for now">
                  <option value={DEFAULT_CREATOR_TYPE}>{DEFAULT_CREATOR_TYPE}</option>
                </select>
                <select className="export-select exp2-cname" value={creatorForm.mixed ? '__mixed' : (creatorForm.value || '')}
                  onChange={e => e.target.value && e.target.value !== '__mixed' && applyField('creator', e.target.value)}>
                  {creatorForm.mixed && <option value="__mixed" disabled>mixed</option>}
                  <option value="">— Select —</option>
                  {[...creators].sort().map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="export-naming-field">
              <label className="staging-field-label">Creative name</label>
              {/* One field for whatever is selected; with several tiles each gets the next number (-1, -2, …) when validated. */}
              <input className="export-input" disabled={!selKeys.length}
                placeholder={nameForm.mixed ? 'mixed — type one name for all' : 'e.g. BananaChallenge'}
                value={selKeys.length && !nameForm.mixed ? (nameForm.value || '') : ''}
                onChange={e => applyField('creativeName', e.target.value)} style={{ width: '100%' }} />
              {activeTags.length > 0 && (
                <div className="exp2-tagline">
                  {activeTags.map(t => (
                    <button key={t.id} className="exp2-tagchip" onClick={() => patchTag(t.id, { active: false })} title={`Turn ${t.label} off`}>{t.label} ✕</button>
                  ))}
                  {tagPreview && <span className="exp2-tagname">→ {tagPreview}</span>}
                </div>
              )}

              {/* [DC] is global to the export */}
              <div className="exp2-dc">
                <label className="exp2-check" title="New idea — hook, selling point, scenario or story. Later batches, iterations and language versions don't get it.">
                  <input type="checkbox" checked={batchDC} onChange={e => setBatchDC(e.target.checked)} />
                  <b>[DC]</b> — new concept, every file of this export
                </label>
              </div>
            </div>

            <div className="export-naming-field">
              <label className="staging-field-label">Feature</label>
              <div className="exp2-feats">
                {[active.slice(0, 4), ...Array.from({ length: Math.ceil(Math.max(active.length - 4, 0) / 5) }, (_, r) => active.slice(4 + r * 5, 9 + r * 5))].map((row, r) => (
                  <div key={r} className={`exp2-frow ${r === 0 ? 'r4' : 'r5'}`}>
                    {row.map(f => { const i = active.indexOf(f); return (
                      <button key={f} className={`export-lang-btn${!featureForm.mixed && featureForm.value === f ? ' active' : ''}`}
                        onClick={() => applyField('feature', f)}>{f}{i < SHORTCUT_COUNT && <kbd className="exp2-kbd">{i + 1}</kbd>}</button>
                    ); })}
                  </div>
                ))}
                {featureForm.mixed && <Mixed />}
              </div>
            </div>

            <div className="export-naming-field">
              <label className="staging-field-label">Persona</label>
              <Seg field="persona" options={personas} />
            </div>

            <div className="export-naming-field">
              <label className="staging-field-label">Hook</label>
              <Seg field="hookType" options={['ugc', 'aigc']} />
              {detected && <div className="exp2-help">Detected from the file: {detected}</div>}
            </div>

            <div className="export-naming-field">
              <label className="staging-field-label">Language</label>
              <Seg field="lang" options={LANGS} />
            </div>

            <div className="exp2-validate-wrap">
              <button className="rbar-go exp2-validate" disabled={!canValidate || exporting} onClick={validateSelection}>
                {selKeys.length ? `Validate ${selKeys.length} file${selKeys.length === 1 ? '' : 's'}` : 'Validate'}
              </button>
              <div className="exp2-help">
                {!selKeys.length ? 'Select tiles, fill the form once, then validate — they move to the bar below.'
                  : !canValidate ? `Needs ${validateMissing}.`
                  : selKeys.length > 1 ? `All ${selKeys.length} get this name, numbered -${validated.length + 1} to -${validated.length + selKeys.length}.` : `Numbered -${validated.length + 1}.`}
              </div>
            </div>
          </div>

          {/* ── Drop zone / output grid (right two thirds) ── */}
          <div className="exp2-zone"
            onDragEnter={e => { e.preventDefault(); dzCounter.current++; setDzDrag(true); }}
            onDragLeave={e => { e.preventDefault(); dzCounter.current--; if (!dzCounter.current) setDzDrag(false); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); dzCounter.current = 0; setDzDrag(false); handleRenameFiles(e.dataTransfer.files); }}
            style={dzDrag ? { outline: '2px dashed var(--accent)', outlineOffset: '-6px' } : undefined}>
            {!hasEntries ? (
              <div className="export-dropzone exp2-empty">
                <input type="file" multiple accept="video/*,.mp4,.mov,.avi,.webm" onChange={e => handleRenameFiles(e.target.files)} />
                <div className="export-drop-title">Drag &amp; drop your videos</div>
                <div className="export-drop-sub">MP4 · Max size: 500MB</div>
                <div className="export-drop-hint">You can upload multiple files</div>
              </div>
            ) : (
              <>
                <div className="exp2-toolbar">
                  <button className="tb-ctl" onClick={selectAll}>{stagedOrder.length > 0 && stagedOrder.every(k => selected.has(k)) ? 'unselect all' : 'select all'}</button>
                  <span className="exp2-sel">{selKeys.length ? `${selKeys.length} selected` : 'Click to select · shift-click for a range'}</span>
                  <span style={{ flex: 1 }} />
                  <input type="range" min={0} max={ZOOM_LEVELS.length - 1} value={zoomLevel} onChange={e => setZoomLevel(+e.target.value)} className="export-zoom-slider" style={{ width: 90 }} />
                </div>
                <div className="export-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                  {stagedOrder.length === 0 && <div className="exp2-allvalidated">Everything is validated — export from the bar below, or add more files.</div>}
                  {entries.map((entry, i) => {
                    const k = order[i];
                    if (validatedSet.has(k)) return null;   // validated files live in the footer queue
                    const st = statusOf(k);
                    const isSel = selected.has(k);
                    const isExported = exportedKeys.has(k);
                    const isProcessing = exporting && exportingKey === k;
                    const isError = errorKeys.has(k);
                    const src = tileSrc(entry);
                    const name = isSel && st.ready ? candidateNames[k] : null;
                    const dcAt = name && batchDC ? name.indexOf('[DC]-') : -1;
                    return (
                      <div key={k}
                        className={`export-grid-cell exp2-tile ${isSel ? 'is-selected' : ''} ${st.ready ? 'is-ready' : 'needs-info'} ${isExported ? 'is-exported' : ''} ${isProcessing ? 'is-processing' : ''} ${isError ? 'is-error' : ''} ${cursor === k ? 'is-cursor' : ''}`}>
                        <div className="export-grid-thumb" onClick={(e) => onTileClick(e, k)}>
                          <video src={src} className="export-grid-video" muted playsInline />
                          {isExported && <div className="export-grid-exported-overlay"><div className="export-grid-exported-check">✓</div></div>}
                          <div className="export-grid-num">{stagedOrder.indexOf(k) + 1}</div>
                          <div className="export-grid-clean-badge exp2-badge">{tileBadge(entry)}</div>
                          <button className="exp2-play" title="Preview"
                            onClick={(e) => { e.stopPropagation(); setPlayingVideo({ src, name: name || entry.label || `#${i + 1}` }); }}>▶</button>
                          <div className={`export-grid-circle ${isSel ? 'selected' : ''} ${isSel && st.ready ? 'staged' : ''}`} />
                        </div>
                        <div className="exp2-name" title={name || undefined}>
                          {name
                            ? (dcAt >= 0 ? <>{name.slice(0, dcAt)}<mark className="exp2-dcmark">[DC]-</mark>{name.slice(dcAt + 5)}</> : name)
                            : <span className="exp2-needs">{isSel ? `needs info · ${st.missing.join(', ')}` : 'not named yet'}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {bigConcept > 0 && (
                  <div className="exp2-nudge">{bigConcept} variations for this concept. The taxonomy caps a concept at 2–3, 5 with AI tools — beyond that it adds noise rather than signal.</div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Footer bar: shared with Studio (components/RenderBar.js) ── */}
        <RenderBar
          thumbs={exportKeys.map(k => ({
            id: k, src: tileSrc(entries[order.indexOf(k)]),
            state: errorKeys.has(k) ? 'error' : exportedKeys.has(k) ? 'ready' : exportingKey === k ? 'rendering' : 'queued',
            title: exporting || exportDone ? candidateNames[k] : 'Click to send back to the grid',
            onClick: exporting || exportDone ? null : () => unvalidate(k),
          }))}
          running={exporting ? { label: `Exporting ${exportProgress.done} / ${exportProgress.total}`, done: exportProgress.done, total: exportProgress.total } : null}
          confirmation={!exporting && exportDone && exportErrors.length === 0 ? `✓ ${exportProgress.total} file${exportProgress.total === 1 ? '' : 's'} exported` : null}
          status={
            exporting ? null
            : exportDone ? (exportErrors.length > 0 ? <span className="rbar-bad"><b>{exportErrors.length}</b> failed — {String(exportErrors[0] || '').slice(0, 90)}</span> : null)
            : !hasEntries ? 'Nothing staged yet'
            : readyCount === 0 ? 'Nothing validated yet'
            : <><b>{readyCount}</b> file{readyCount === 1 ? '' : 's'} ready to export</>
          }
          actions={<>
            {exportDone && exportErrors.length > 0 && <button className="tb-ctl" onClick={() => runExport()}>Retry</button>}
            {!exporting && !exportDone && readyCount > 0 && <button className="rbar-back" onClick={() => setValidated([])}>Clear queue</button>}
            <button className="rbar-back" disabled={exporting} onClick={backToStudio}>← Back to Studio</button>
            {!exportDone && !exporting && (
              <button className="rbar-go" onClick={runExport} disabled={readyCount === 0}>
                {readyCount === 0 ? 'Export' : `Export ${readyCount} file${readyCount === 1 ? '' : 's'}`}
              </button>
            )}
          </>}
        />
      </div>

      {/* ── Naming sheet: the single place to manage the convention, creators, features and platform tags ── */}
      {showNamingModal && (
        <div className="modal-backdrop" onClick={() => setShowNamingModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title"># Naming</span><button className="modal-close" onClick={() => setShowNamingModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="export-lang-row" style={{ marginBottom: 14 }} role="tablist">
                {[['convention', 'Convention'], ['creators', 'Creators'], ['features', 'Features'], ['tags', 'Platform tags']].map(([id, label]) => (
                  <button key={id} role="tab" aria-selected={namingSection === id} className={`export-lang-btn${namingSection === id ? ' active' : ''}`}
                    onClick={() => setNamingSection(id)}>{label}</button>
                ))}
              </div>

              {namingSection === 'convention' && (
                <>
                  <div className="exp2-model">{NAMING_MODEL}</div>
                  <div className="exp2-help" style={{ margin: '8px 0 12px' }}>
                    Blocks are joined with <b>_</b>, details inside a block with <b>-</b>. A skipped block is a bare <b>-</b>. <b>[DC]-</b> goes at the front of the creative-name block (see docs/taxonomy.md).
                  </div>
                  <label className="staging-field-label">Personas</label>
                  <div className="creator-modal-list">
                    {personas.map(p => <div key={p} className="creator-modal-row"><span>{p}</span><button className="delete-btn" onClick={() => removePersonaFn(p)}>✕</button></div>)}
                  </div>
                  <div className="creator-modal-add">
                    <input className="settings-input" placeholder="Persona…" value={newPersona} onChange={e => setNewPersona(e.target.value)} onKeyDown={e => e.key === 'Enter' && addPersonaFn()} />
                    <button className="add-voice-btn" onClick={addPersonaFn}>+ Add</button>
                  </div>
                </>
              )}

              {namingSection === 'creators' && (
                <>
                  <div className="exp2-help" style={{ marginBottom: 8 }}>Creator type: <b>{DEFAULT_CREATOR_TYPE}</b> (the only type available for now).</div>
                  {creators.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-dim)', fontSize: 13 }}>No creators — add your influencer names below</div>
                  )}
                  <div className="creator-modal-list">
                    {[...creators].sort().map(c => (
                      <div key={c} className="creator-modal-row">
                        <span>{c}</span>
                        <button className="delete-btn" onClick={() => removeCreatorFn(c)}>✕</button>
                      </div>
                    ))}
                  </div>
                  <div className="creator-modal-add">
                    <input className="settings-input" placeholder="Creator / influencer name..."
                      value={newCreator} onChange={e => setNewCreator(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCreatorFn()} />
                    <button className="add-voice-btn" onClick={addCreatorFn}>+ Add</button>
                  </div>
                </>
              )}

              {namingSection === 'features' && (
                <>
                  <div className="exp2-help" style={{ marginBottom: 8 }}>Hiding a feature only removes it from the picker — the parser, the spend report and historical names still know all {ALL_FEATURES.length}. The first {SHORTCUT_COUNT} active ones carry the keys 1–{SHORTCUT_COUNT}.</div>
                  <div className="creator-modal-list">
                    {[...active, ...ALL_FEATURES.filter(f => !active.includes(f))].map(f => (
                      <div key={f} className="creator-modal-row">
                        <label className="exp2-check" style={{ flex: 1 }}><input type="checkbox" checked={active.includes(f)} onChange={() => toggleFeatureActive(f)} /> {f}</label>
                        {active.includes(f) && <>
                          <button className="export-icon-btn" onClick={() => moveFeature(f, -1)} title="Move up">↑</button>
                          <button className="export-icon-btn" onClick={() => moveFeature(f, 1)} title="Move down">↓</button>
                        </>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {namingSection === 'tags' && (
                <>
                  <div className="exp2-help" style={{ margin: '0 0 6px' }}>An active tag applies to every file of this export. A tag with a suffix appends it to the creative name; tags you add here don't touch the name.</div>
                  <div className="creator-modal-list">
                    {platformTags.map(t => (
                      <div key={t.id} className="creator-modal-row">
                        <label className="exp2-check" title={t.active ? 'Active' : 'Off'}>
                          <input type="checkbox" checked={t.active} onChange={() => patchTag(t.id, { active: !t.active })} />
                        </label>
                        <input className="settings-input" style={{ flex: 1 }} value={t.label} aria-label={`Rename ${t.label}`}
                          onChange={e => patchTag(t.id, { label: e.target.value })}
                          onBlur={e => { if (!e.target.value.trim()) patchTag(t.id, { label: t.id }); }} />
                        {t.suffix && <span className="exp2-help">+{t.suffix}</span>}
                      </div>
                    ))}
                  </div>
                  <div className="creator-modal-add">
                    <input className="settings-input" placeholder="Platform tag…" value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTagFn()} />
                    <button className="add-voice-btn" onClick={addTagFn}>+ Add</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
